/**
 * Audio Showcase - Interactive Audio Player with Animated Waveforms
 * Similar to Instagram voice notes
 */

class AudioShowcase {
  constructor() {
    this.currentlyPlaying = null;
    this.init();
  }

  init() {
    const audioCards = document.querySelectorAll('[data-audio-card]');
    
    audioCards.forEach((card) => {
      const playButton = card.querySelector('[data-play-button]');
      const audioElement = card.querySelector('[data-audio-source]');
      const waveformBars = card.querySelectorAll('.waveform-bar');
      
      if (!audioElement || !playButton || waveformBars.length === 0) {
        return;
      }

      // Store references
      card.audioPlayer = {
        audio: audioElement,
        button: playButton,
        bars: Array.from(waveformBars),
        isPlaying: false,
        animationFrame: null
      };

      // Play button click handler
      playButton.addEventListener('click', (e) => {
        e.stopPropagation();
        this.togglePlay(card);
      });

      // Card click handler (also triggers play/pause)
      card.addEventListener('click', () => {
        this.togglePlay(card);
      });

      // Audio ended handler
      audioElement.addEventListener('ended', () => {
        this.stopAudio(card);
      });

      // Audio error handler
      audioElement.addEventListener('error', () => {
        card.classList.add('error');
        console.error('Error loading audio file');
      });

      // Audio loaded handler
      audioElement.addEventListener('loadedmetadata', () => {
        card.classList.remove('loading');
      });

      // Update waveform as audio plays
      audioElement.addEventListener('timeupdate', () => {
        this.updateWaveform(card);
      });
    });
  }

  togglePlay(card) {
    const player = card.audioPlayer;
    
    if (!player || !player.audio) return;

    // Stop any other playing audio
    if (this.currentlyPlaying && this.currentlyPlaying !== card) {
      this.stopAudio(this.currentlyPlaying);
    }

    if (player.isPlaying) {
      this.pauseAudio(card);
    } else {
      this.playAudio(card);
    }
  }

  playAudio(card) {
    const player = card.audioPlayer;
    
    if (!player) return;

    player.audio.play().then(() => {
      player.isPlaying = true;
      player.button.classList.add('playing');
      card.classList.add('playing');
      this.currentlyPlaying = card;
      
      // Start waveform animation
      this.animateWaveform(card);
    }).catch((error) => {
      console.error('Error playing audio:', error);
      card.classList.add('error');
    });
  }

  pauseAudio(card) {
    const player = card.audioPlayer;
    
    if (!player) return;

    player.audio.pause();
    player.isPlaying = false;
    player.button.classList.remove('playing');
    card.classList.remove('playing');
    
    // Stop animation frame
    if (player.animationFrame) {
      cancelAnimationFrame(player.animationFrame);
    }
  }

  stopAudio(card) {
    const player = card.audioPlayer;
    
    if (!player) return;

    player.audio.pause();
    player.audio.currentTime = 0;
    player.isPlaying = false;
    player.button.classList.remove('playing');
    card.classList.remove('playing');
    
    // Reset waveform
    player.bars.forEach((bar) => {
      bar.classList.remove('active');
    });
    
    // Stop animation frame
    if (player.animationFrame) {
      cancelAnimationFrame(player.animationFrame);
    }
    
    if (this.currentlyPlaying === card) {
      this.currentlyPlaying = null;
    }
  }

  updateWaveform(card) {
    const player = card.audioPlayer;
    
    if (!player || !player.audio.duration) return;

    const currentTime = player.audio.currentTime;
    const duration = player.audio.duration;
    const progress = currentTime / duration;
    const totalBars = player.bars.length;
    const activeBars = Math.floor(progress * totalBars);

    // Update bar states based on progress
    player.bars.forEach((bar, index) => {
      if (index < activeBars) {
        bar.classList.add('active');
      } else {
        bar.classList.remove('active');
      }
    });
  }

  animateWaveform(card) {
    const player = card.audioPlayer;
    
    if (!player || !player.isPlaying) return;

    // Create smooth animation loop
    const animate = () => {
      if (player.isPlaying) {
        this.updateWaveform(card);
        player.animationFrame = requestAnimationFrame(animate);
      }
    };

    animate();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new AudioShowcase();
  });
} else {
  new AudioShowcase();
}

// Pause all audio when navigating away
window.addEventListener('beforeunload', () => {
  document.querySelectorAll('[data-audio-source]').forEach((audio) => {
    audio.pause();
  });
});
